import { useState, useMemo } from "react";
import { formatINR, formatNumber } from "@/lib/units";
import ResultsPanel, { CalcPageHeader, Field } from "@/components/ResultsPanel";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function WarehouseRental() {
  const [area, setArea] = useState("20000");
  const [rate, setRate] = useState("22");
  const [months, setMonths] = useState("60");
  const [deposit, setDeposit] = useState("10");
  const [esc, setEsc] = useState("5");

  const data = useMemo(() => {
    const A = Number(area)||0;
    const R = Number(rate)||0;
    const monthly = A * R;
    const escalation = (Number(esc)||0) / 100;
    const years = 5;
    let cumulative = 0;
    const proj = [];
    let annual = monthly * 12;
    for (let y = 1; y <= years; y++) {
      cumulative += annual;
      proj.push({ year: `Y${y}`, annual, cumulative });
      annual = annual * (1 + escalation);
    }
    const security = monthly * (Number(deposit)||0);
    const totalTerm = proj.reduce((s, p, i) => i < Math.min(years, Math.ceil((Number(months)||0)/12)) ? s + p.annual : s, 0);
    return { monthly, annual: monthly * 12, proj, security, totalTerm };
  }, [area, rate, months, deposit, esc]);

  const rows = [
    ["Warehouse Area", `${formatNumber(Number(area)||0)} sq ft`],
    ["Rate per sq ft", `₹${rate}/mo`],
    ["Monthly Rent", formatINR(data.monthly)],
    ["Annual Rent (Y1)", formatINR(data.annual)],
    ["Security Deposit", formatINR(data.security)],
    ["5-Year Cumulative", formatINR(data.proj[4]?.cumulative || 0)],
    ["Total Term Revenue", formatINR(data.totalTerm)],
  ];

  return (
    <div>
      <CalcPageHeader title="Warehouse Rental Calculator" description="Monthly & annual rent with security deposit and 5-year escalation projection." slug="warehouse-rent" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-7 space-y-6">
          <div className="rounded-lg border border-border bg-card p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Area (sq ft)"><input data-testid="wh-area" type="number" value={area} onChange={(e)=>setArea(e.target.value)} className="w-full h-11 px-3 rounded-md border border-border bg-background font-mono outline-none focus:ring-2 focus:ring-primary/40" /></Field>
            <Field label="Rate ₹/sq ft/month"><input data-testid="wh-rate" type="number" value={rate} onChange={(e)=>setRate(e.target.value)} className="w-full h-11 px-3 rounded-md border border-border bg-background font-mono outline-none focus:ring-2 focus:ring-primary/40" /></Field>
            <Field label="Lease Period (months)"><input data-testid="wh-months" type="number" value={months} onChange={(e)=>setMonths(e.target.value)} className="w-full h-11 px-3 rounded-md border border-border bg-background font-mono outline-none focus:ring-2 focus:ring-primary/40" /></Field>
            <Field label="Security Deposit (× monthly)"><input data-testid="wh-deposit" type="number" value={deposit} onChange={(e)=>setDeposit(e.target.value)} className="w-full h-11 px-3 rounded-md border border-border bg-background font-mono outline-none focus:ring-2 focus:ring-primary/40" /></Field>
            <Field label="Annual Escalation %"><input data-testid="wh-esc" type="number" value={esc} onChange={(e)=>setEsc(e.target.value)} className="w-full h-11 px-3 rounded-md border border-border bg-background font-mono outline-none focus:ring-2 focus:ring-primary/40" /></Field>
          </div>
          <div className="rounded-lg border border-border bg-card p-5 sm:p-6">
            <div className="overline mb-3">5-Year cumulative rent</div>
            <div className="h-64" data-testid="wh-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.proj}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                  <XAxis dataKey="year" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v)=>`₹${(v/100000).toFixed(0)}L`} />
                  <Tooltip formatter={(v)=>formatINR(v)} contentStyle={{background:"hsl(var(--card))",border:"1px solid hsl(var(--border))",borderRadius:6}} />
                  <Line type="monotone" dataKey="cumulative" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{fill:"hsl(var(--primary))",r:4}} />
                  <Line type="monotone" dataKey="annual" stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} strokeDasharray="4 4" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <ResultsPanel title="Warehouse Rental" slug="warehouse-rent" highlight={{ label: "Monthly Rent", value: formatINR(data.monthly), sub: `Annual: ${formatINR(data.annual)}` }} rows={rows} onReset={()=>{setArea("");setRate("");}} />
        </div>
      </div>
    </div>
  );
}
